import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryThumbnails: Record<string, string> = {
  'Shoes': 'https://readdy.ai/api/search-image?query=Single%20stylish%20sneaker%20on%20clean%20cream%20background%2C%20minimalist%20product%20photography%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones&width=80&height=80&seq=nav-shoes-v2&orientation=squarish',
  'Hoodies / Sweaters': 'https://readdy.ai/api/search-image?query=Folded%20grey%20hoodie%20on%20warm%20cream%20surface%2C%20minimalist%20product%20photography%2C%20soft%20lighting%2C%20neutral%20warm%20tones&width=80&height=80&seq=nav-hoodies-v2&orientation=squarish',
  'T-Shirts': 'https://readdy.ai/api/search-image?query=Folded%20white%20t-shirt%20on%20warm%20beige%20surface%2C%20minimalist%20photography%2C%20soft%20natural%20light%2C%20clean%20composition&width=80&height=80&seq=nav-tshirts-v2&orientation=squarish',
  'Jackets': 'https://readdy.ai/api/search-image?query=Lightweight%20jacket%20folded%20on%20warm%20cream%20background%2C%20minimalist%20product%20photography%2C%20soft%20lighting%2C%20neutral%20tones&width=80&height=80&seq=nav-jackets-v2&orientation=squarish',
  'Pants / Shorts': 'https://readdy.ai/api/search-image?query=Folded%20casual%20pants%20on%20warm%20beige%20surface%2C%20minimalist%20photography%2C%20soft%20natural%20light%2C%20clean%20composition&width=80&height=80&seq=nav-pants-v2&orientation=squarish',
  'Headwear': 'https://readdy.ai/api/search-image?query=Baseball%20cap%20on%20clean%20cream%20background%2C%20minimalist%20product%20photography%2C%20soft%20lighting%2C%20warm%20neutral%20tones&width=80&height=80&seq=nav-headwear-v2&orientation=squarish',
  'Sets': 'https://readdy.ai/api/search-image?query=Matching%20clothing%20set%20folded%20on%20warm%20beige%20surface%2C%20minimalist%20photography%2C%20soft%20light%2C%20clean%20composition&width=80&height=80&seq=nav-sets-v2&orientation=squarish',
  'Underwear / Underpants': 'https://readdy.ai/api/search-image?query=Folded%20cotton%20underwear%20on%20warm%20cream%20surface%2C%20minimalist%20photography%2C%20soft%20lighting%2C%20neutral%20tones&width=80&height=80&seq=nav-underwear-v2&orientation=squarish',
  'Jersey': 'https://readdy.ai/api/search-image?query=Sports%20jersey%20on%20warm%20beige%20background%2C%20minimalist%20product%20photography%2C%20soft%20natural%20light%2C%20clean%20composition&width=80&height=80&seq=nav-jersey-v2&orientation=squarish',
  'Accessories': 'https://readdy.ai/api/search-image?query=Watch%20and%20bracelet%20on%20warm%20cream%20surface%2C%20minimalist%20photography%2C%20soft%20lighting%2C%20neutral%20warm%20tones&width=80&height=80&seq=nav-accessories-v2&orientation=squarish',
  'Bags': 'https://readdy.ai/api/search-image?query=Stylish%20tote%20bag%20on%20warm%20beige%20background%2C%20minimalist%20product%20photography%2C%20soft%20light%2C%20neutral%20tones&width=80&height=80&seq=nav-bags-v2&orientation=squarish',
  'Belts': 'https://readdy.ai/api/search-image?query=Leather%20belt%20on%20warm%20cream%20surface%2C%20minimalist%20photography%2C%20soft%20natural%20light%2C%20clean%20composition&width=80&height=80&seq=nav-belts-v2&orientation=squarish',
  'Perfumes': 'https://readdy.ai/api/search-image?query=Elegant%20perfume%20bottle%20on%20warm%20beige%20surface%2C%20minimalist%20photography%2C%20soft%20lighting%2C%20neutral%20tones&width=80&height=80&seq=nav-perfumes-v2&orientation=squarish',
  'Other Stuff': 'https://readdy.ai/api/search-image?query=Sunglasses%20and%20wallet%20on%20warm%20cream%20background%2C%20minimalist%20photography%2C%20soft%20light%2C%20clean%20composition&width=80&height=80&seq=nav-other-v2&orientation=squarish',
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeAll = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-lg border-b border-background-200/70'
            : 'bg-background-50/85 backdrop-blur-sm'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <a
              href="/"
              className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 tracking-tight whitespace-nowrap cursor-pointer hover:text-primary-500 transition-colors"
              onClick={(e) => { e.preventDefault(); navigate('/'); closeAll(); }}
            >
              VigorBuy
            </a>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <a
                href="/"
                className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap"
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
              >
                Home
              </a>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Categories
                  <span className={`w-3.5 h-3.5 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-xs" />
                  </span>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-background-50 rounded-xl border border-background-200/80 py-2 w-72 max-h-[420px] overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                          <img
                            src={categoryThumbnails[cat.name] || categoryThumbnails['Other Stuff']}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <span className="whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="/article/vigorbuy-spreadsheet-guide"
                className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap"
                onClick={(e) => { e.preventDefault(); navigate('/article/vigorbuy-spreadsheet-guide'); }}
              >
                Shopping Guide
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:inline-flex items-center px-4 py-2 text-sm font-semibold border-2 border-foreground-900 text-foreground-900 rounded-full hover:bg-foreground-900 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                VigorBuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>

              {/* Hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-900 cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
            onClick={closeAll}
          />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-background-50 overflow-y-auto">
            <div className="p-6 pt-24">
              <div className="space-y-2">
                <a
                  href="/"
                  className="flex items-center px-4 py-3 text-base font-semibold text-foreground-900 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={(e) => { e.preventDefault(); navigate('/'); closeAll(); }}
                >
                  <span className="w-7 h-7 flex items-center justify-center mr-3 text-foreground-700">
                    <i className="ri-home-4-line text-lg" />
                  </span>
                  Home
                </a>

                <a
                  href="/article/vigorbuy-spreadsheet-guide"
                  className="flex items-center px-4 py-3 text-base font-semibold text-foreground-900 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={(e) => { e.preventDefault(); navigate('/article/vigorbuy-spreadsheet-guide'); closeAll(); }}
                >
                  <span className="w-7 h-7 flex items-center justify-center mr-3 text-foreground-700">
                    <i className="ri-book-open-line text-lg" />
                  </span>
                  Shopping Guide
                </a>
              </div>

              <div className="mt-6 pt-6 border-t border-background-200/70">
                <p className="px-4 text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3">
                  Categories
                </p>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 rounded-xl transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={categoryThumbnails[cat.name] || categoryThumbnails['Other Stuff']}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}